import { useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { AutoHeightRnD } from "@nova/component-auto-height-rnd"
import { zIndex } from "@nova/constants"
import * as Icons from "@nova/component-icons"
import {
    DistanceUnit,
    formatAltitude,
    formatSpeed,
    metersToKilometers,
    metersToMiles,
} from "@nova/format"
import { calculateTrigonmetricDistance } from "@nova/calculate-geospatial"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectUserLocationShareById } from "features/entity-caching/flights/selectors"
import {
    selectActiveWorkspaceId,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { zoomToShowPoint } from "features/map/control/slice"
import { fetchTelemetryEvents } from "tools/telemetry/fetchTelemetryEvents"
import { useTelemetryPlayback } from "tools/hooks/useTelemetryPlayback"
import { PlaybackControls } from "components/PlaybackControls"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import {
    userLocationSharePlaybackStore,
    UserLocationShareTelemetryEvent,
} from "features/map/layers/user-locations/userLocationSharePlaybackStore"
import { selectFollowingEntityId } from "plugins/shared/playback/state/selectors"
import {
    openPlayback,
    closePlayback,
    setFollowingEntity,
} from "plugins/shared/playback/state/slice"
import {
    selectOpenUserLocationShareIds,
    selectIsUserLocationShareNodeEnabled,
    selectIsUserLocationSharePathEnabled,
} from "../state/selectors"
import { toggleUserLocationShareNode, toggleUserLocationSharePath } from "../state/slice"
import { InspectorPanelAccordion } from "features/map/inspector-panel/accordion/InspectorPanelAccordion"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"

const MIN_WIDTH = 400
const MAX_STARTING_OFFSET = 32 * 5
const STORE_COMMIT_INTERVAL_MS = 66

export const UserLocationShareContainer = ({ id }: { id: string }) => {
    const dispatch = useAppDispatch()
    const entity = useAppSelector(selectUserLocationShareById(id))
    const members = useAppSelector(selectWorkspaceMembers)
    const member = members.find((m) => m.userId === entity.createdBy?.userId)
    const openIds = useAppSelector(selectOpenUserLocationShareIds)
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const isFollowing = useAppSelector(selectFollowingEntityId) === id
    const nodeEnabled = useAppSelector(selectIsUserLocationShareNodeEnabled(id))
    const pathEnabled = useAppSelector(selectIsUserLocationSharePathEnabled(id))

    const contentRef = useRef<HTMLDivElement>(null)
    const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false)
    const [width, setWidth] = useState(MIN_WIDTH)
    const [position, setPosition] = useState({
        x: 8 + Math.min((openIds.length - 1) * 32, MAX_STARTING_OFFSET),
        y: 8 + Math.min((openIds.length - 1) * 32, MAX_STARTING_OFFSET),
    })

    const [sortedEvents, setSortedEvents] = useState<UserLocationShareTelemetryEvent[] | null>(null)
    useEffect(() => {
        if (!orgId || !projectId) return
        let isMounted = true
        fetchTelemetryEvents({ orgId, projectId, id }).then(({ events }) => {
            if (!isMounted) return
            const locationEvents = (events as UserLocationShareTelemetryEvent[])
                .filter((e): e is UserLocationShareTelemetryEvent => e.type === "locationShare")
                .toSorted((a, b) => a.timestamp - b.timestamp)
            setSortedEvents(locationEvents)
        })
        return () => {
            isMounted = false
        }
    }, [orgId, projectId, id])

    const timestamps = useMemo(() => sortedEvents?.map((e) => e.timestamp) ?? [], [sortedEvents])
    const {
        playbackIndex,
        isPlaying,
        speed,
        handlePlayPause,
        handleSeek,
        handleSeekStart,
        handleSeekEnd,
        handleSetSpeed,
    } = useTelemetryPlayback({
        timestamps,
        autoPlay: true,
    })

    const currentIndex =
        sortedEvents && sortedEvents.length > 0
            ? Math.min(Math.round(playbackIndex), sortedEvents.length - 1)
            : undefined
    const currentEvent =
        sortedEvents && currentIndex !== undefined ? sortedEvents[currentIndex] : undefined

    const cumulativeDistancesMeters = useMemo(() => {
        if (!sortedEvents) return []
        const distances = [0]
        for (let i = 1; i < sortedEvents.length; i++) {
            distances.push(
                distances[i - 1] +
                    calculateTrigonmetricDistance(sortedEvents[i - 1], sortedEvents[i])
            )
        }
        return distances
    }, [sortedEvents])

    const isMetricDistance =
        distanceUnit === DistanceUnit.Meters || distanceUnit === DistanceUnit.Kilometers
    const distanceUnitLabel = isMetricDistance ? "km" : "mi"
    const toDisplayDistance = (meters: number) =>
        isMetricDistance ? metersToKilometers(meters) : metersToMiles(meters)
    const currentDistanceMeters =
        currentIndex !== undefined ? (cumulativeDistancesMeters[currentIndex] ?? 0) : 0
    const totalDistanceMeters = cumulativeDistancesMeters.at(-1) ?? 0

    const lastCommittedAtRef = useRef(0)
    useEffect(() => {
        if (!sortedEvents || !currentEvent) return
        const isEdge =
            !isPlaying || currentEvent === sortedEvents[0] || currentEvent === sortedEvents.at(-1)
        const now = Date.now()
        if (!isEdge && now - lastCommittedAtRef.current < STORE_COMMIT_INTERVAL_MS) return
        lastCommittedAtRef.current = now
        userLocationSharePlaybackStore.set(id, {
            path: sortedEvents,
            current: currentEvent,
            isPlaying,
        })
    }, [currentEvent, isPlaying, sortedEvents, id])

    useEffect(() => {
        return () => {
            userLocationSharePlaybackStore.remove(id)
        }
    }, [id])

    if (!entity) return null

    return (
        <div
            className="absolute pointer-events-none inset-0"
            style={{ zIndex: zIndex.LiveVideo }}
            onMouseDown={() => dispatch(openPlayback({ id, type: "locationShare" }))}
        >
            <AutoHeightRnD
                position={position}
                setPosition={setPosition}
                width={width}
                minWidth={MIN_WIDTH}
                setWidth={setWidth}
                contentRef={contentRef}
                className="border border-white-200"
                dragHandleClassName=".user-location-share-drag-handle"
            >
                <div className="flex flex-col gap-4">
                    <div className="user-location-share-drag-handle flex gap-2 items-center w-full cursor-grab">
                        <div className="flex items-center gap-1.5">
                            <div className="relative group/tooltip w-7 h-7">
                                <div className="w-7 h-7 rounded-full overflow-hidden">
                                    <ProfilePhoto
                                        userId={member?.userId ?? ""}
                                        fullName={member?.fullName ?? "Unknown User"}
                                    />
                                </div>
                                <Tooltip title={member?.fullName ?? "Unknown User"} />
                            </div>
                            <div className="text-white text-base">
                                {entity.name || "Location Share"}
                            </div>
                        </div>
                        <div className="relative group/tooltip">
                            <Icons.SmallIcon
                                Icon={
                                    isFollowing
                                        ? Icons.AssistantNavigationFilled
                                        : Icons.AssistantNavigation
                                }
                                className={classNames("cursor-pointer", {
                                    "text-gray-100 hover:text-white": !isFollowing,
                                    "text-blue-500": isFollowing,
                                })}
                                onClick={() =>
                                    dispatch(
                                        setFollowingEntity(
                                            isFollowing ? undefined : { type: "locationShare", id }
                                        )
                                    )
                                }
                            />
                            <Tooltip title={isFollowing ? "Stop following" : "Follow"} />
                        </div>
                        <div className="relative group/tooltip">
                            <Icons.ExtraSmallIcon
                                Icon={Icons.ZoomTo}
                                className="text-gray-100 hover:text-gray-0 cursor-pointer"
                                onClick={() => {
                                    if (!currentEvent) return
                                    dispatch(
                                        zoomToShowPoint({
                                            lng: currentEvent.longitude,
                                            lat: currentEvent.latitude,
                                        })
                                    )
                                }}
                            />
                            <Tooltip title="Zoom to location" />
                        </div>
                        <div className="grow" />
                        <Icons.SmallIcon
                            Icon={Icons.XClose}
                            className="cursor-pointer text-gray-100 hover:text-gray-0"
                            onClick={() => dispatch(closePlayback({ id }))}
                        />
                    </div>
                    {sortedEvents === null ? (
                        <div className="px-4 py-3 text-gray-400 text-sm">Loading telemetry...</div>
                    ) : sortedEvents.length === 0 ? (
                        <div className="px-4 py-3 text-gray-400 text-sm">No telemetry recorded</div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <PlaybackControls
                                playbackIndex={playbackIndex}
                                isPlaying={isPlaying}
                                timestamps={timestamps}
                                onPlayPause={handlePlayPause}
                                onSeek={handleSeek}
                                onSeekStart={handleSeekStart}
                                onSeekEnd={handleSeekEnd}
                                speed={speed}
                                onSpeedChange={handleSetSpeed}
                            />
                            <div className="grid grid-cols-3 gap-2 rounded-lg border border-gray-600 divide-x divide-gray-600">
                                <div className="px-3 py-2 flex flex-col gap-0.5">
                                    <span className="text-gray-300 text-xs">Distance</span>
                                    <span className="text-white text-sm">
                                        {toDisplayDistance(currentDistanceMeters).toFixed(2)} /{" "}
                                        {toDisplayDistance(totalDistanceMeters).toFixed(2)}{" "}
                                        {distanceUnitLabel}
                                    </span>
                                </div>
                                <div className="px-3 py-2 flex flex-col gap-0.5">
                                    <span className="text-gray-300 text-xs">Speed</span>
                                    <span className="text-white text-sm">
                                        {formatSpeed({
                                            value: currentEvent?.speed ?? 0,
                                            unit: distanceUnit,
                                        })}
                                    </span>
                                </div>
                                <div className="px-3 py-2 flex flex-col gap-0.5">
                                    <span className="text-gray-300 text-xs">Elevation</span>
                                    <span className="text-white text-sm">
                                        {formatAltitude({
                                            value: currentEvent?.altitude ?? 0,
                                            unit: distanceUnit,
                                        })}
                                    </span>
                                </div>
                            </div>
                            <InspectorPanelAccordion
                                isOpen={isMoreOptionsOpen}
                                setIsOpen={setIsMoreOptionsOpen}
                                title="More Options"
                                shouldPadChildrenVertically={false}
                                extraVerticalPaddingInRem={0.5}
                                className="!mx-0"
                            >
                                <div className="my-4 mx-4">
                                    <div className="h-8 flex items-center">
                                        <small className="xs text-gray-0">Visibility</small>
                                    </div>
                                    <div className="flex gap-5">
                                        <div className="flex-1">
                                            <InspectorPanelKeyValueToggle
                                                keyTitle="Node"
                                                isToggled={nodeEnabled}
                                                setIsToggled={() =>
                                                    dispatch(toggleUserLocationShareNode({ id }))
                                                }
                                                toggleClassName="justify-between"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <InspectorPanelKeyValueToggle
                                                keyTitle="Path"
                                                isToggled={pathEnabled}
                                                setIsToggled={() =>
                                                    dispatch(toggleUserLocationSharePath({ id }))
                                                }
                                                toggleClassName="justify-between"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </InspectorPanelAccordion>
                        </div>
                    )}
                </div>
            </AutoHeightRnD>
        </div>
    )
}
